import React, { useState, useEffect, useRef } from 'react';

import { useNavigate, useLocation } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';

import { 

  UserCircle,

  ClipboardList,
  ClipboardCheck,

  Settings,

  ChevronRight,

  Users,

  Shield,

  Menu,

  X,

  LogOut,

  Bell,

  Receipt,

  BarChart3

} from 'lucide-react';



const ROLE_LABELS = {

  admin: 'Administrator',

  manager: 'Manager',

  therapist: 'Therapist',

  wmf: 'WMF Staff'

};



const NavigationItem = ({ 

  icon: Icon, 

  title, 

  path, 

  isActive, 

  subItems = [],

  onNavigate 

}) => {

  const [isExpanded, setIsExpanded] = useState(false);

  const location = useLocation();

  

  const handleClick = () => {

    if (subItems.length > 0) {

      setIsExpanded(!isExpanded);

    } else if (path) {

      onNavigate(path);

    }

  };



  return (

    <div>

      <button

        onClick={handleClick}

        className={`

          w-full flex items-center px-3 py-2 text-sm font-medium rounded-md

          transition-colors duration-150 ease-in-out group

          ${isActive 

            ? 'bg-blue-50 text-blue-700 shadow-sm' 

            : 'text-slate-600 hover:bg-slate-50'

          }

        `}

      >

        <Icon className={`h-5 w-5 mr-3 flex-shrink-0 ${

          isActive ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-500'

        }`} />

        <span className="flex-1 text-left">{title}</span>

        {subItems.length > 0 && (

          <ChevronRight 

            className={`h-4 w-4 transform transition-transform duration-200

              ${isExpanded ? 'rotate-90' : ''}

              ${isActive ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-500'}

            `}

          />

        )}

      </button>

      

      {isExpanded && subItems.length > 0 && (

        <div className="ml-4 mt-1 space-y-1">

          {subItems.map((item) => (

            <button

              key={item.name}

              onClick={() => onNavigate(item.path)}

              className={`

                w-full text-left px-3 py-2 text-sm rounded-md

                ${location.pathname === item.path

                  ? 'bg-blue-50 text-blue-700'

                  : 'text-slate-600 hover:bg-slate-50'

                }

              `}

            >

              {item.name}

            </button>

          ))}

        </div>

      )}

    </div>

  );

};



const DashboardHeader = () => {

  const { user, logout } = useAuth();

  const navigate = useNavigate();

  const [showDropdown, setShowDropdown] = useState(false);

  const dropdownRef = useRef(null);



  // Handle click outside to close dropdown

  useEffect(() => {

    const handleClickOutside = (event) => {

      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {

        setShowDropdown(false);

      }

    };



    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);

  }, []);



  const handleLogout = async () => {

    try {

      await logout();

      navigate('/', { replace: true });

    } catch (error) {

      console.error('Logout failed:', error);

    }

  };



  return (

    <div className="flex items-center justify-between px-6 py-3 bg-white border-b border-slate-200 w-full">

      <div className="flex items-center space-x-4">

        <img

          src="/300x300.jpg"

          alt="HH Logo"

          className="h-12 w-12 rounded-lg"

        />

        <div className="hidden md:block">

          <h1 className="text-lg font-semibold text-slate-900">HH Online</h1>

        </div>

      </div>

      

      <div className="flex items-center space-x-4">

        <div className="text-right">

          <p className="text-sm font-medium text-slate-900">

            Hi, {user?.firstName || 'Guest'}

          </p>

          <p className="text-xs text-slate-500">

            {ROLE_LABELS[user?.role] || 'Unknown Role'}

          </p>

        </div>

        <div className="relative" ref={dropdownRef}>

          <button

            onClick={() => setShowDropdown(!showDropdown)}

            className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center hover:bg-slate-300 transition-colors duration-200"

          >

            <UserCircle className="h-6 w-6 text-slate-600" />

          </button>



          {/* Dropdown Menu */}

          {showDropdown && (

            <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-100 z-50">

              <div className="py-1">

                <div className="px-4 py-2 text-sm text-gray-700">

                  <span className="block font-medium">{user?.firstName} {user?.lastName}</span>

                  <span className="block text-xs text-gray-500">{user?.email}</span>

                </div>

              </div>

              <div className="py-1">

                <button

                  onClick={handleLogout}

                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"

                >

                  <LogOut className="h-4 w-4 mr-2" />

                  Logout

                </button>

              </div>

            </div>

          )}

        </div>

      </div>

    </div>

  );

};



const DashboardLayout = ({ userRole = 'therapist', children }) => {

  const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth >= 768);

  const [showLogoutAlert, setShowLogoutAlert] = useState(false);

  const navigate = useNavigate();

  const location = useLocation();

  const { logout, user } = useAuth();

  

  // Check if user is admin or manager
  const isAdmin = user?.role === 'admin';
  const isManagerOrAdmin = user?.role === 'admin' || user?.role === 'manager';



  const handleLogout = async () => {

    try {

      await logout();

      navigate('/', { replace: true });

    } catch (error) {

      console.error('Logout failed:', error);

    }

  };



  // Navigation items configuration...

  const navigationItems = [

    {

      title: 'Students',

      icon: Users,

      path: '/dashboard/students',

      subItems: [

        {

          name: 'My Students',

          path: '/dashboard/students/my-students',

          roles: ['therapist', 'manager', 'admin']

        },

        {

          name: 'All Students',

          path: '/dashboard/students/all',

          roles: ['therapist', 'manager', 'admin']

        },

        {

          name: 'Add Student',

          path: '/dashboard/students/add',

          roles: ['manager', 'admin']

        }

      ]

    },

    {

      title: 'Session Reports',

      icon: ClipboardList,

      path: '/dashboard/reports',

      subItems: [

        {

          name: 'Create Report',

          path: '/dashboard/reports/create',

          roles: ['therapist', 'manager', 'admin']

        },

        {

          name: 'View Reports',

          path: '/dashboard/reports/view',

          roles: ['therapist', 'manager', 'admin']

        }

      ]

    },

    {

      title: 'Assessments',

      icon: ClipboardCheck,

      path: '/dashboard/assessments',

      subItems: [

        {

          name: 'My Assessments',

          path: '/dashboard/assessments',

          roles: ['therapist', 'manager', 'admin']

        }

      ]

    },

    {

      title: 'Program Metrics',

      icon: BarChart3,

      path: '/dashboard/metrics',

      subItems: [

        {

          name: 'Overview',

          path: '/dashboard/metrics',

          roles: ['wmf', 'therapist', 'manager', 'admin']

        },

        {

          name: 'Compare Instruments',

          path: '/dashboard/metrics/cross-instrument',

          roles: ['wmf', 'therapist', 'manager', 'admin']

        }

      ]

    },

  ];



  if (isManagerOrAdmin) {

    navigationItems.push({

      title: 'Invoicing',

      icon: Receipt,

      path: '/dashboard/invoicing',

      subItems: [

        {

          name: 'New Run / History',

          path: '/dashboard/invoicing',

          roles: ['manager', 'admin']

        },

        {

          name: 'Manage Retreats',

          path: '/dashboard/invoicing/retreats',

          roles: ['manager', 'admin']

        }

      ]

    });

  }



  if (isAdmin) {

    navigationItems.push({

      title: 'Administration',

      icon: Shield,

      path: '/dashboard/admin',

      subItems: [

        {

          name: 'Manage Users',

          path: '/dashboard/admin/therapists',

          roles: ['admin']

        },

        {

          name: 'API Key Management',

          path: '/dashboard/admin/api-key',

          roles: ['admin']

        },

        {

          name: 'API Test Suite',

          path: '/dashboard/admin/api-tests',

          roles: ['admin']

        },

        {

          name: 'Security Alerts',

          path: '/dashboard/admin/security',

          roles: ['admin']

        },

        {

          name: 'System Settings',

          path: '/dashboard/admin/settings',

          roles: ['admin']

        }

      ]

    });

  }



  const handleNavigation = (path) => {

    navigate(path);

    if (window.innerWidth < 768) {

      setIsSidebarOpen(false);

    }

  };



  if (!user) {

    return null;

  }



  return (

    <div className="min-h-screen bg-slate-50">

      {/* Top Navigation Bar */}

      <header className="bg-white border-b border-slate-200 fixed top-0 left-0 right-0 z-30">

        <div className="h-14 flex items-center justify-between px-4">

          <div className="flex items-center">

            <button

              onClick={() => setIsSidebarOpen(!isSidebarOpen)}

              className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 md:hidden"

            >

              {isSidebarOpen ? (

                <X className="h-5 w-5" />

              ) : (

                <Menu className="h-5 w-5" />

              )}

            </button>

          </div>

          

          {/* Add Dashboard Header */}

          <DashboardHeader />

        </div>

      </header>



      <div className="pt-14 flex h-[calc(100vh-3.5rem)]">

        {/* Rest of the layout content... */}

        <aside className={`

          fixed md:sticky top-14 h-[calc(100vh-3.5rem)] bg-white border-r border-slate-200 overflow-hidden

          transition-all duration-300 ease-in-out z-20

          ${isSidebarOpen ? 'w-64 translate-x-0' : 'w-0 -translate-x-full md:w-64 md:translate-x-0'}

        `}>

          <div className="h-full flex flex-col">

            <nav className="flex-1 px-3 py-3 space-y-1 overflow-y-auto">

              {navigationItems

                .map((item) => ({

                  ...item,

                  subItems: item.subItems.filter(subItem =>

                    subItem.roles.includes(user.role)

                  )

                }))

                .filter((item) => item.subItems.length > 0)

                .map((item) => (

                <NavigationItem

                  key={item.title}

                  icon={item.icon}

                  title={item.title}

                  path={item.path}

                  isActive={location.pathname.startsWith(item.path)}

                  subItems={item.subItems}

                  onNavigate={handleNavigation}

                />

              ))}

            </nav>

            

            <div className="p-3 border-t border-slate-200">

              <button 

                onClick={() => setShowLogoutAlert(true)}

                className="w-full flex items-center px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md"

              >

                <LogOut className="h-5 w-5 mr-3 text-slate-400" />

                Sign Out

              </button>

            </div>

          </div>

        </aside>



        <main className="flex-1 overflow-auto bg-slate-50 min-w-0">

          <div className="p-4 md:p-6 max-w-7xl mx-auto">

            {children}

          </div>

        </main>



        {/* Mobile Overlay */}

        {isSidebarOpen && (

          <div 

            className="fixed inset-0 bg-slate-900/50 md:hidden z-10"

            onClick={() => setIsSidebarOpen(false)}

          />

        )}

      </div>



      {/* Logout Confirmation Dialog */}

      {showLogoutAlert && (

        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">

          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm mx-4 w-full">

            <h3 className="text-lg font-semibold text-slate-900 mb-2">

              Confirm Logout

            </h3>

            <p className="text-slate-600 mb-4">

              Are you sure you want to sign out? Any unsaved changes will be lost.

            </p>

            <div className="flex justify-end space-x-3">

              <button

                onClick={() => setShowLogoutAlert(false)}

                className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-800 focus:outline-none"

              >

                Cancel

              </button>

              <button

                onClick={handleLogout}

                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"

              >

                Sign Out

              </button>

            </div>

          </div>

        </div>

      )}

    </div>

  );

};



export default DashboardLayout;
